import { ComponentProps, forwardRef, PropsWithChildren, ReactNode } from 'react'
import { styled } from '../../config'
import { Spinner } from '../Spinner'

const Root = styled('button', {
  outline: 'none',
  border: 'none',
  display: 'flex',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontFamily: 'inherit',
  alignItems: 'center',
  flexShrink: 0,
  justifyContent: 'center',
  boxSizing: 'border-box',
  transition: 'all 100ms ease',
  cursor: 'pointer',
  '&:disabled': {
    pointerEvents: 'none'
  },
  variants: {
    uppercase: {
      true: { textTransform: 'uppercase' }
    },
    rounded: {
      sm: { borderRadius: '4px !important' },
      lg: { borderRadius: '32px !important' },
      full: { borderRadius: '$round !important' }
    },
    wide: { true: { padding: '12px 32px !important' } },
    height: { full: { height: '100%' } },
    width: { full: { width: '100%' }, fit: { width: 'fit-content' } },
    size: {
      lg: {
        fontSize: '16px',
        lineHeight: '16px',
        padding: '16px 24px',
        borderRadius: '10px',
        fontWeight: 500
      },
      md: {
        fontSize: '14px',
        lineHeight: '16px',
        padding: '12px 16px',
        borderRadius: '10px',
        fontWeight: 500
      },
      sm: {
        fontSize: '12px',
        lineHeight: '16px',
        padding: '8px 16px',
        borderRadius: '8px',
        fontWeight: 500
      },
      xs: {
        fontSize: '12px',
        lineHeight: '20px',
        padding: '2px 12px',
        borderRadius: '8px',
        fontWeight: 500
      }
    },
    variant: {
      primary: {
        background: '$gray1000',
        borderWidth: '1px',
        borderColor: '$gray1000',
        borderStyle: 'solid',
        color: '$white',
        '&:disabled': { background: '$blackA050', borderColor: '$blackA050', color: '$blackA300' },
        '&:hover': { background: '$gray0700', borderColor: '$gray0700' },
        '&:active': { background: '$gray0600', borderColor: '$gray0600' }
      },
      primaryInverted: {
        background: '$white',
        borderWidth: '1px',
        borderColor: '$white',
        borderStyle: 'solid',
        color: '$black',
        '&:disabled': { background: '$whiteA050', borderColor: '$whiteA050', color: '$whiteA300' },
        '&:hover': { background: '$whiteA700', borderColor: '$whiteA700' },
        '&:active': { background: '$whiteA600', borderColor: '$whiteA600' }
      },
      secondary: {
        background: 'transparent',
        borderStyle: 'solid',
        borderColor: '$blackA400',
        borderWidth: '1px',
        color: '$black',
        '&:disabled': { borderColor: '$blackA150', color: '$blackA300' },
        '&:hover': { borderColor: '$blackA700' },
        '&:active': { borderColor: '$black' }
      },
      secondaryInverted: {
        background: 'transparent',
        borderStyle: 'solid',
        borderColor: '$whiteA100',
        borderWidth: '1px',
        color: '$white',
        '&:disabled': { borderColor: '$whiteA150', color: '$whiteA300' },
        '&:hover': { borderColor: '$whiteA500' },
        '&:active': { borderColor: '$white' }
      },
      success: {
        background: '$indigo0600',
        color: '$white',
        '&:disabled': { background: '$blackA050', color: '$blackA300' },
        '&:hover': { background: '$indigo0500' },
        '&:active': { background: '$indigo0400' }
      },
      ghost: {
        background: 'transparent',
        color: '$black',
        '&:disabled': { color: '$blackA300' },
        '&:hover': { background: '$blackA050' },
        '&:active': { background: '$blackA100' }
      },
      danger: {
        background: '$red0600',
        color: '$white',
        '&:disabled': { background: '$blackA050', color: '$blackA300' },
        '&:hover': { background: '$red0500' },
        '&:active': { background: '$red0400' }
      },
      light: {
        background: '$white',
        color: '$black',
        '&:disabled': { background: '$whiteA300', color: '$blackA400' },
        '&:hover': { background: '$whiteA500' },
        '&:active': { background: '$whiteA400' }
      },
      outlineDark: {
        background: 'transparent',
        color: '$white',
        borderColor: '$white',
        borderWidth: '2px',
        borderStyle: 'solid',
        '&:hover': { background: '$whiteA100' },
        '&:disabled': { borderColor: '$whiteA300', color: '$whiteA300' }
      },
      joinNow: {
        background: '#76FFE6',
        color: '$black',
        '&:disabled': { opacity: '70%' },
        '&:hover': { opacity: '80%' },
        '&:active': { opacity: '80%' }
      },
      ghostIndigo: {
        background: 'transparent',
        border: 'none',
        color: '$indigo0600',
        '&:disabled': { color: '$whiteA300' },
        '&:hover': { color: '$indigo0400' },
        '&:active': { color: '$indigo0300' }
      },
      ghostInverted: {
        background: 'transparent',
        color: '$white',
        '&:disabled': { color: '$whiteA300' },
        '&:hover': { background: '$whiteA050' },
        '&:active': { background: '$whiteA100' }
      },
      opensea: {
        background: '#1C3D60',
        color: '$white',
        '&:disabled': { color: '$white' },
        '&:hover': { color: '$white' },
        '&:active': { color: '$white' }
      },
      aiPrimary: {
        borderRadius: '4px',
        border: '1px solid $gray0100',
        backgroundColor: '$gray0800',
        color: '$gray0400',
        '&:hover': {
          backgroundColor: '$gray0700'
        }
      },
      aiOutline: {
        borderRadius: '4px',
        border: '1px solid $whiteA300',
        background: 'transparent',
        color: '$white',
        '&:hover': { borderColor: '$whiteA800' },
        '&:disabled': { borderColor: '$whiteA300', color: '$whiteA300' }
      },
      aiGhostInverted: {
        backgroundColor: '$whiteA050',
        borderRadius: '4px',
        color: '$whiteA800',
        '&:hover': {
          backgroundColor: '$whiteA100'
        }
      },
      aiSuccess: {
        background: 'linear-gradient(90deg, #79F9FB 0%, #90FEAD 100%)',
        color: '$blackA900',
        borderRadius: '4px',
        '&:hover': {
          color: '$blackA600'
        }
      },
      aiSuccessOutline: {
        color: '#79F9FB',
        border: '1px solid #79F9FB',
        background: 'transparent',
        borderRadius: '4px',
        '&:hover': {
          backgroundColor: '$whiteA025'
        }
      },
      aiPremiumSecondary: {
        background: 'transparent',
        border: '1px solid #E099FA',
        color: '#E099FA',
        borderRadius: '4px',
        '&:hover': {
          color: '$black',
          background: '#E099FA'
        }
      },
      aiPremiumPrimary: {
        background: '#E099FA',
        color: '$black',
        borderRadius: '4px',
        '&:hover': {
          background: '#E099FA'
        },
        '&:disabled': {
          backgroundColor: 'rgb(224 153 250 / 60%)',
          color: '$blackA300'
        }
      },
      link: {
        background: 'transparent',
        border: 'none',
        color: '$blue0500',
        '&:disabled': { color: '$whiteA300' },
        '&:hover': { color: '$blue0400' },
        '&:active': { color: '$blue0300' }
      }
    }
  },
  defaultVariants: {
    size: 'md',
    variant: 'primary'
  }
})

const Prefix = styled('span', {
  flexShrink: 0,
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginRight: '8px',
  color: 'inherit'
})

const Postfix = styled('span', {
  flexShrink: 0,
  marginLeft: '4px',
  justifyContent: 'center',
  alignItems: 'center',
  color: 'inherit',
  display: 'inline-flex'
})

type ButtonProps = ComponentProps<typeof Root> & {
  prefixIcon?: ReactNode
  postfixIcon?: ReactNode
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, PropsWithChildren<ButtonProps>>(
  ({ prefixIcon, postfixIcon, loading, children, disabled, ...props }, ref) => {
    const prefixElement =
      (loading && (
        <Prefix css={children ? {} : { marginRight: 0 }}>
          <Spinner />
        </Prefix>
      )) ||
      (prefixIcon && <Prefix>{prefixIcon}</Prefix>)
    const postFixElement = postfixIcon && <Postfix>{postfixIcon}</Postfix>
    return (
      <Root ref={ref} disabled={disabled || loading} {...props}>
        {prefixElement}
        <span>{children}</span>
        {postFixElement}
      </Root>
    )
  }
)
